import type {
  FmRoutineChecklistItemDto,
  FmWorkOrderDto,
  RecordChecklistResponseRequest,
} from './services-work-order.models';

export type ChecklistLangCode = 'en' | 'hi' | 'bn' | 'ta' | 'te' | 'kn';

export const CHECKLIST_LANG_CODES: readonly ChecklistLangCode[] = [
  'en',
  'hi',
  'bn',
  'ta',
  'te',
  'kn',
] as const;

export function isChecklistLangCode(raw: string | null | undefined): raw is ChecklistLangCode {
  if (raw == null) {
    return false;
  }
  const code = raw.trim().toLowerCase();
  return (CHECKLIST_LANG_CODES as readonly string[]).includes(code);
}

export interface WorkOrderChecklistRow {
  order: number;
  /** Display text for the active language (falls back to English when translation is missing). */
  text: string;
  /** True when English exists but selected language translation is missing (English shown instead). */
  translationMissing: boolean;
  photoRequired: boolean;
  responseType?: string;
  measurementUnit?: string;
  measurementMin?: number;
  measurementMax?: number;
  optional?: boolean;
  skipped?: boolean;
  skipNote?: string | null;
  completedAt: string | null;
  responseText: string | null;
  imageUrl: string | null;
}

export interface ChecklistDraftEntry {
  responseText: string;
  imageUrl: string;
  skipped?: boolean;
  skipNote?: string;
  /** Filesystem-relative path under Directory.Data when photo is not yet uploaded. */
  localPhotoPath?: string;
}

export function checklistLangLabel(code: ChecklistLangCode): string {
  switch (code) {
    case 'hi':
      return 'हिन्दी';
    case 'bn':
      return 'বাংলা';
    case 'ta':
      return 'தமிழ்';
    case 'te':
      return 'తెలుగు';
    case 'kn':
      return 'ಕನ್ನಡ';
    default:
      return 'English';
  }
}

/** Single banner when one or more items fall back to English. */
export function checklistMissingTranslationNotice(lang: ChecklistLangCode): string {
  if (lang === 'en') {
    return '';
  }
  return (
    `Translation is not available in ${checklistLangLabel(lang)} for some checklist items; ` +
    'English is shown instead.'
  );
}

function textMap(raw: FmRoutineChecklistItemDto['text']): Record<string, string> {
  if (raw == null) {
    return {};
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t ? { en: t } : {};
  }
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(raw)) {
    const lang = k.trim().toLowerCase();
    const val = (v ?? '').trim();
    if (val && isChecklistLangCode(lang)) {
      out[lang] = val;
    }
  }
  return out;
}

export function buildChecklistRows(
  w: FmWorkOrderDto | null | undefined,
  lang: ChecklistLangCode = 'en',
): WorkOrderChecklistRow[] {
  if (!w) {
    return [];
  }
  const snapshot: FmRoutineChecklistItemDto[] = Array.isArray(w.checklistSnapshot) ? w.checklistSnapshot : [];
  const responses = (w.checklistResponses ?? {}) as Record<
    string,
    { responseText?: string; imageUrl?: string; completedAt?: string; skipped?: boolean; skipNote?: string }
  >;
  return snapshot.map((item) => {
    const r = responses[String(item.order)] ?? null;
    const texts = textMap(item.text);
    const en = texts['en'] ?? '';
    const localized = texts[lang] ?? '';
    const translationMissing = en.length > 0 && lang !== 'en' && localized.length === 0;
    return {
      order: item.order,
      text: localized || en,
      translationMissing,
      photoRequired: !!item.photoRequired,
      responseType: item.responseType,
      measurementUnit: item.measurementUnit,
      measurementMin: item.measurementMin,
      measurementMax: item.measurementMax,
      optional: !!item.optional,
      skipped: !!r?.skipped,
      skipNote: r?.skipNote ?? null,
      completedAt: r?.completedAt ?? null,
      responseText: r?.responseText ?? null,
      imageUrl: r?.imageUrl ?? null,
    };
  });
}

export function buildChecklistDrafts(rows: WorkOrderChecklistRow[]): Record<number, ChecklistDraftEntry> {
  const out: Record<number, ChecklistDraftEntry> = {};
  for (const row of rows) {
    out[row.order] = {
      responseText: row.responseText ?? '',
      imageUrl: row.imageUrl ?? '',
      skipped: !!row.skipped,
      skipNote: row.skipNote ?? '',
      localPhotoPath: '',
    };
  }
  return out;
}

/** True when the draft has either a remote URL or a queued local photo file. */
export function draftHasPhoto(draft: ChecklistDraftEntry | undefined): boolean {
  if (!draft) {
    return false;
  }
  return Boolean(draft.imageUrl?.trim() || draft.localPhotoPath?.trim());
}

export function sanitizeMeasurementInput(raw: string): string {
  if (!raw) {
    return '';
  }
  let out = '';
  let hasDot = false;
  let hasMinus = false;
  for (const ch of String(raw)) {
    if (ch >= '0' && ch <= '9') {
      out += ch;
      continue;
    }
    if (ch === '.' && !hasDot) {
      hasDot = true;
      out += ch;
      continue;
    }
    if (ch === '-' && out.length === 0 && !hasMinus) {
      hasMinus = true;
      out += ch;
    }
  }
  return out;
}

export function isMeasurementRow(row: WorkOrderChecklistRow): boolean {
  return row.responseType?.toUpperCase() === 'MEASUREMENT';
}

export interface ChecklistValidationIssue {
  order: number;
  message: string;
}

export function findFirstChecklistValidationIssue(
  rows: WorkOrderChecklistRow[],
  drafts: Record<number, ChecklistDraftEntry>,
): ChecklistValidationIssue | null {
  for (const row of rows) {
    const draft = drafts[row.order] ?? { responseText: '', imageUrl: '', localPhotoPath: '' };
    const responseText = draft.responseText.trim();
    const hasPhoto = draftHasPhoto(draft);
    if (row.optional && draft.skipped) {
      if (!draft.skipNote?.trim()) {
        return { order: row.order, message: `Item ${row.order}: add a skip note.` };
      }
      continue;
    }
    const photoType = row.responseType?.toUpperCase() === 'PHOTO';
    const yesNoType = row.responseType?.toUpperCase() === 'YES_NO';
    if ((row.photoRequired || photoType) && !hasPhoto) {
      return { order: row.order, message: `Item ${row.order}: a photo is required.` };
    }
    if (yesNoType && !responseText) {
      return { order: row.order, message: `Item ${row.order}: choose Yes or No.` };
    }
    if (isMeasurementRow(row)) {
      if (!responseText) {
        return { order: row.order, message: `Item ${row.order}: enter a numeric measurement.` };
      }
      if (!Number.isFinite(Number(responseText))) {
        return { order: row.order, message: `Item ${row.order}: enter a valid number.` };
      }
    } else if (!responseText && !hasPhoto) {
      return { order: row.order, message: `Item ${row.order}: please provide a response.` };
    }
  }
  return null;
}

export function measurementInlineError(text: string, row: WorkOrderChecklistRow): string | null {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }
  if (!Number.isFinite(Number(trimmed)) || trimmed === '-' || trimmed === '.' || trimmed === '-.') {
    return 'Enter numbers only.';
  }
  return null;
}

export function draftsToRecordRequests(
  rows: WorkOrderChecklistRow[],
  drafts: Record<number, ChecklistDraftEntry>,
): RecordChecklistResponseRequest[] {
  return rows.map((row) => {
    const draft = drafts[row.order] ?? { responseText: '', imageUrl: '' };
    const body: RecordChecklistResponseRequest = { order: row.order };
    if (row.optional && draft.skipped) {
      body.skipped = true;
      const note = draft.skipNote?.trim();
      if (note) {
        body.skipNote = note;
      }
      return body;
    }
    const responseText = draft.responseText.trim();
    const imageUrl = draft.imageUrl.trim();
    if (responseText) {
      body.responseText = responseText;
    }
    if (imageUrl) {
      body.imageUrl = imageUrl;
    }
    return body;
  });
}
